import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { adminLogin, adminSetup, adminCheckExists } from '@/lib/api';
import { Settings, Lock, User, ArrowLeft } from 'lucide-react';
import InstallPWA from '@/components/InstallPWA';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // If already logged in, forward straight to /admin/dashboard (preserve any query
  // string so notification deep-links like /admin?approve=<id> keep working).
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      const search = window.location.search || '';
      navigate('/admin/dashboard' + search, { replace: true });
    }
  }, [navigate]);

  // Check if admin exists on load
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await adminCheckExists();
        setNeedsSetup(!response.data.exists);
      } catch (error) {
        // If check fails, assume setup needed
        setNeedsSetup(true);
      } finally {
        setCheckingSetup(false);
      }
    };
    checkAdmin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (needsSetup) {
        response = await adminSetup({ username, password });
        toast.success('Admin account created successfully!');
      } else {
        response = await adminLogin({ username, password });
        toast.success('Login successful!');
      }
      
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUsername', response.data.username || username);
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden flex items-center justify-center">
      {/* Ghost Text Background */}
      <div className="ghost-text">NAKI TUNE 2.0</div>
      
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-400 hover:text-white transition-colors z-20"
        data-testid="back-to-home"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm uppercase tracking-wider">Back</span>
      </button>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="glass p-8 border border-zinc-800 bg-zinc-900/80">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-green-600 flex items-center justify-center">
              <Settings className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="brand-title text-2xl uppercase tracking-wide text-white">
                NAKI TUNE 2.0
              </h1>
              <p className="text-xs text-zinc-500 uppercase tracking-[0.15em]">Admin Panel</p>
            </div>
          </div>

          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white text-center mb-2">
            {needsSetup ? 'Create Admin Account' : 'Admin Login'}
          </h2>
          
          {needsSetup && (
            <p className="text-zinc-400 text-sm text-center mb-6">
              Set up your admin username and password
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Username
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono h-12"
                placeholder={needsSetup ? "Choose a username" : "Enter username"}
                data-testid="admin-username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-zinc-950 border-zinc-800 focus:border-green-600 font-mono h-12"
                placeholder={needsSetup ? "Choose a password" : "Enter password"}
                data-testid="admin-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 text-black font-bold uppercase tracking-wider py-6 btn-glow"
              data-testid="admin-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  {needsSetup ? 'Creating...' : 'Logging in...'}
                </span>
              ) : (
                needsSetup ? 'Create Account' : 'Login'
              )}
            </Button>
          </form>
        </div>
      </div>
      {/* PWA Install - admin only */}
      <InstallPWA />
    </div>
  );
}
